import { useCallback } from "react";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { Workspace } from "../../../../types/workspace";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../../../components/Snackbar";

const TEXT = {
  SAVE_WORKSPACE_NOT_EDITABLE_TOOLTIP_DISABLED:
    "You don't have permission to save changes",
  SAVE_WORKSPACE_SUCCESS: "Project updated successfully",
  SAVE_WORKSPACE_ERROR: "Failed to update project",
  SAVE_WORKSPACE_AS_NEW_SUCCESS: "Project created!",
  EDIT_CONFIGURATION_TOOLTIP_DISABLED:
    "You don't have permission to edit this project",
};

export const useSaveWorkspace = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();

  const saveWorkspace = useWorkspaceStore((state) => state.saveWorkspace);
  const saveAsNewWorkspace = useWorkspaceStore(
    (state) => state.saveAsNewWorkspace
  );
  const discardChanges = useWorkspaceStore((state) => state.discardChanges);
  const isSaving = useWorkspaceStore((state) => state.isSaving);
  const hasUnsavedChanges = useWorkspaceStore(
    (state) => state.hasUnsavedChanges
  );
  const userCanSave = useWorkspaceStore(
    (state) => state.draft?.permissions.can_write
  );

  const handleSave = useCallback(async () => {
    if (!userCanSave) {
      showError(TEXT.SAVE_WORKSPACE_NOT_EDITABLE_TOOLTIP_DISABLED);
      return;
    }

    try {
      await saveWorkspace();
      showSuccess(TEXT.SAVE_WORKSPACE_SUCCESS);
    } catch (error) {
      console.error(error);
      showError(TEXT.SAVE_WORKSPACE_ERROR);
    }
  }, [saveWorkspace, showError, showSuccess, userCanSave]);

  const saveAsNewSuccessCallback = useCallback(
    async (createdWorkspace: Workspace, openNewWorkspace: boolean = true) => {
      showSuccess(TEXT.SAVE_WORKSPACE_AS_NEW_SUCCESS);
      if (openNewWorkspace) {
        setTimeout(() => {
          navigate(`/projects/${createdWorkspace.id}`);
        }, 1000);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showSuccess]
  );

  const handleSaveAsNew = useCallback(
    async (openNewWorkspace: boolean = true) => {
      return saveAsNewWorkspace(
        async (data: Workspace) =>
          await saveAsNewSuccessCallback(data, openNewWorkspace)
      );
    },
    [saveAsNewWorkspace, saveAsNewSuccessCallback]
  );

  const getEditConfigTooltip = useCallback(() => {
    if (userCanSave) return "";

    return TEXT.EDIT_CONFIGURATION_TOOLTIP_DISABLED;
  }, [userCanSave]);

  return {
    handleSave,
    handleSaveAsNew,
    isSaving,
    hasUnsavedChanges,
    userCanSave,
    getEditConfigTooltip,
    discardChanges,
  };
};
